import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { resetPasswordErrorMessage } from '@/utils/resetPasswordErrorMessage';
import { useT } from '@/i18n/labels';
import { useThemeStore } from '@/stores/themeStore';
import { dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';

// Plain-text nav link styled to sit under the card's action button. Inline
// style (not a new CSS class) so it stays within the "reuse existing classes
// only" rule for this task while still reading as a link, not a button.
const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--accent)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * Public / pre-auth. Kicks off a self-serve password reset (ADR-012,
 * WS-5.A). The backend is NON-ENUMERATING — POST /api/identity/forgot-password
 * always resolves 200 whether or not the account exists, so this page always
 * shows the same generic success copy and never reveals account existence.
 * Renders outside <ConsoleShell/>, so it applies the design tokens to its own
 * root (mirrors LoginPage).
 */
export const ForgotPasswordPage: React.FC = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = useThemeStore((s) => s.mode);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);

  const [tenantId, setTenantId] = useState(searchParams.get('tenant') ?? '');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axiosClient.post(API.AUTH.FORGOT_PASSWORD, { tenantId, email, language: lang });
      setSubmitted(true);
    } catch (err) {
      setError(resetPasswordErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
      <form onSubmit={handleSubmit} className="ac-card ac-login-card" data-testid="forgot-password-form">
        <div className="ac-login-title">{t('forgotPwTitle')}</div>
        <div className="ac-login-sub">{t('forgotPwSubtitle')}</div>

        {error && (
          <div role="alert" className="ac-login-err">
            {error}
          </div>
        )}

        {submitted ? (
          <p data-testid="forgot-password-success">{t('forgotPwSuccess')}</p>
        ) : (
          <>
            <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
              <label htmlFor="forgotTenantId" className="ac-set-field-label">
                {t('loginCompanyCode')}
              </label>
              <input
                id="forgotTenantId"
                type="text"
                className="ac-field"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
              />
            </div>

            <div className="ac-set-field-group" style={{ marginBottom: 22 }}>
              <label htmlFor="forgotEmail" className="ac-set-field-label">
                {t('forgotPwEmail')}
              </label>
              <input
                id="forgotEmail"
                type="email"
                className="ac-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <button type="submit" disabled={loading} className="ac-btn primary" style={{ width: '100%' }}>
              {loading ? t('forgotPwSubmitting') : t('forgotPwSubmit')}
            </button>
          </>
        )}

        <p style={{ marginTop: 14, marginBottom: 0, textAlign: 'center' }}>
          <button type="button" onClick={() => navigate('/login')} style={linkButtonStyle}>
            {t('forgotPwBackToLogin')}
          </button>
        </p>
      </form>
    </div>
  );
};

export default ForgotPasswordPage;
